import {
    LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { AlertTriangle } from "lucide-react";

/**
 * ChartFromJson — renders the ```chart``` JSON payload produced by the AI in
 * Graph output mode. Schemas supported:
 *
 *   { "type": "line"        | "payoff" | "yield_curve",
 *     "title": "...", "x_label": "...", "y_label": "...",
 *     "series": [ { "name": "P&L", "data": [ {"x":15,"y":-100}, ... ] } ] }
 *
 *   { "type": "bar",
 *     "series": [ { "name": "Return", "data": [ {"x":"Jan","y":3.2}, ... ] } ] }
 *
 *   { "type": "weights",
 *     "series": [ { "data": [ {"name":"SCOM","value":42}, ... ] } ] }
 *
 * If `disabled=true` (channel !== "graph") we render the raw JSON in a muted
 * code block so users can still see the model's intent without a big chart.
 */
const PIE_COLORS = ["#0284c7", "#f97316", "#10b981", "#8b5cf6", "#f43f5e", "#eab308", "#14b8a6", "#f59e0b"];

export default function ChartFromJson({ spec, raw, disabled }) {
    if (!spec || !spec.type) {
        return (
            <div className="border border-amber-500/30 bg-amber-500/5 rounded-md p-2 text-xs text-amber-700 dark:text-amber-400 inline-flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5" />
                Chart payload could not be parsed.
            </div>
        );
    }
    if (disabled) {
        return (
            <details className="border border-border rounded-md bg-muted/40 text-xs" data-testid="assistant-chart-disabled">
                <summary className="px-2 py-1 cursor-pointer font-semibold">
                    Chart hidden — switch Output to “Graph” to render {spec.title || spec.type}
                </summary>
                <pre className="p-2 overflow-x-auto text-[11px] font-mono">{raw}</pre>
            </details>
        );
    }

    const title = spec.title || "";
    const xLabel = spec.x_label || "";
    const yLabel = spec.y_label || "";
    const type = spec.type;

    if (type === "weights") {
        const data = (spec.series?.[0]?.data || []).map((d) => ({
            name: d.name || d.x || "—",
            value: Number(d.value ?? d.y ?? 0),
        }));
        return (
            <ChartWrapper title={title} testId="assistant-chart-weights">
                <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                        <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                            {data.map((d, i) => <Cell key={d.name || `slice-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </ChartWrapper>
        );
    }

    // line / payoff / yield_curve / bar → shared cartesian pipeline
    const series = (spec.series || []).map((s, i) => ({
        name: s.name || `Series ${i + 1}`,
        color: PIE_COLORS[i % PIE_COLORS.length],
        data: (s.data || []).map((d) => ({ x: d.x, y: Number(d.y) })),
    }));
    // Merge series onto a shared x-axis by index.
    const merged = mergeSeries(series);

    const isBar = type === "bar";
    const ChartTag = isBar ? BarChart : LineChart;
    const SeriesTag = isBar ? Bar : Line;

    return (
        <ChartWrapper title={title} testId={`assistant-chart-${type}`}>
            <ResponsiveContainer width="100%" height={220}>
                <ChartTag data={merged} margin={{ top: 8, right: 12, bottom: 24, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis
                        dataKey="x"
                        label={xLabel ? { value: xLabel, position: "insideBottom", offset: -8, fontSize: 10 } : undefined}
                        tick={{ fontSize: 10 }}
                    />
                    <YAxis
                        label={yLabel ? { value: yLabel, angle: -90, position: "insideLeft", fontSize: 10 } : undefined}
                        tick={{ fontSize: 10 }}
                    />
                    <Tooltip />
                    {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
                    {series.map((s) => (
                        <SeriesTag key={s.name} dataKey={s.name} stroke={s.color} fill={s.color} dot={false} />
                    ))}
                </ChartTag>
            </ResponsiveContainer>
        </ChartWrapper>
    );
}

function ChartWrapper({ title, testId, children }) {
    return (
        <div className="border border-border rounded-md bg-background p-2" data-testid={testId}>
            {title && (
                <div className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground mb-1 px-1">
                    {title}
                </div>
            )}
            {children}
        </div>
    );
}

// Combines multiple series into a single row-per-x dataset for recharts.
function mergeSeries(series) {
    const byX = new Map();
    for (const s of series) {
        for (const pt of s.data) {
            const key = String(pt.x);
            if (!byX.has(key)) byX.set(key, { x: pt.x });
            byX.get(key)[s.name] = pt.y;
        }
    }
    // Preserve insertion order (Map does this natively).
    return Array.from(byX.values());
}
